/**
 * Graph utility functions for building ARIA tree structure
 *
 * @package @alchemiesolns/svg-interactive
 */

import { MolecularWorkspace } from '@alchemiesolns/molecular-workspace';

/**
 * Find all connected components (molecules) in the workspace
 */
export function findConnectedComponents(
  workspace: MolecularWorkspace
): Array<{ atomIds: string[]; bondIds: string[] }> {
  // Track which atoms have been visited
  const visited = new Set<string>();
  const components: Array<{ atomIds: string[]; bondIds: string[] }> = [];

  // Build adjacency list
  const adjacency = new Map<string, Set<string>>();
  const atomToBonds = new Map<string, Set<string>>();

  for (const atomId of workspace.getAtomIds()) {
    adjacency.set(atomId, new Set());
    atomToBonds.set(atomId, new Set());
  }

  for (const bond of workspace.getAllBonds()) {
    const bondId = bond.id;
    const a1 = bond.a1.id;
    const a2 = bond.a2.id;
    adjacency.get(a1)?.add(a2);
    adjacency.get(a2)?.add(a1);
    atomToBonds.get(a1)?.add(bondId);
    atomToBonds.get(a2)?.add(bondId);
  }

  // BFS to find each connected component
  for (const startAtomId of workspace.getAtomIds()) {
    if (visited.has(startAtomId)) continue;

    const componentAtoms: string[] = [];
    const componentBonds = new Set<string>();
    const queue = [startAtomId];

    while (queue.length > 0) {
      const atomId = queue.shift()!;
      if (visited.has(atomId)) continue;

      visited.add(atomId);
      componentAtoms.push(atomId);

      // Add all bonds involving this atom
      const atomBonds = atomToBonds.get(atomId);
      if (atomBonds) {
        for (const bondId of atomBonds) {
          componentBonds.add(bondId);
        }
      }

      // Queue neighbors
      const neighbors = adjacency.get(atomId);
      if (neighbors) {
        for (const neighborId of neighbors) {
          if (!visited.has(neighborId)) {
            queue.push(neighborId);
          }
        }
      }
    }

    components.push({
      atomIds: componentAtoms,
      bondIds: Array.from(componentBonds),
    });
  }

  return components;
}
